import React, { useEffect, useState } from "react";
import { useReactTable, getCoreRowModel, flexRender, getSortedRowModel } from "@tanstack/react-table";
import { fetchLeads, fetchStatuses, updateLead, createCalendarEvent, deleteLead } from "./api";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { useNavigate } from "react-router-dom";

const LeadTable = ({ refresh, highlightedLeadId, prioritySettings }) => {
  const [leads, setLeads] = useState([]);
  const [editingCell, setEditingCell] = useState({ id: null, field: null });
  const [editValue, setEditValue] = useState("");
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("Tutti");
  const [statuses, setStatuses] = useState([]);

  const navigate = useNavigate();

  useEffect(() => {
    loadLeads();
    loadStatuses();
  }, [refresh]);

  const loadLeads = async () => {
    try {
      const data = await fetchLeads();
      setLeads(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("❌ Errore fetchLeads:", err);
      setLeads([]);
    }
  };

  const loadStatuses = async () => {
    try {
      const data = await fetchStatuses();
      setStatuses(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("❌ Errore fetchStatuses:", err);
      setStatuses([]);
    }
  };

  const startEditing = (id, field, initialValue) => {
    setEditingCell({ id, field });
    setEditValue(initialValue);
  };

  const stopEditing = async () => {
    const leadToUpdate = leads.find((lead) => lead.id === editingCell.id);
    if (leadToUpdate && editValue !== leadToUpdate[editingCell.field]) {
      const updatedLead = { ...leadToUpdate, [editingCell.field]: editValue };
      await updateLead(leadToUpdate.id, updatedLead);
      setLeads((prev) => prev.map((lead) => (lead.id === updatedLead.id ? updatedLead : lead)));
    }
    setEditingCell({ id: null, field: null });
    setEditValue("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") stopEditing();
  };

  const handleStatusChange = async (lead, newStatus) => {
    const updatedLead = { ...lead, stato: newStatus };
    await updateLead(lead.id, updatedLead);
    setLeads((prev) => prev.map((l) => (l.id === updatedLead.id ? updatedLead : l)));
  };

  const handleAddToCalendar = async (lead) => {
    const data = prompt("Inserisci data (YYYY-MM-DD)", lead.data_scadenza ? lead.data_scadenza.split("T")[0] : "");
    const start = prompt("Inserisci ora di inizio (HH:MM)", "10:00");
    const end = prompt("Inserisci ora di fine (HH:MM)", "10:30");

    if (!data || !start || !end) {
      alert("⚠️ Devi inserire data e orari!");
      return;
    }

    try {
      const startDateTime = new Date(`${data}T${start}`);
      const endDateTime = new Date(`${data}T${end}`);

      const result = await createCalendarEvent({
        summary: `Meeting con ${lead.nome}`,
        description: lead.note || "Appuntamento CRM",
        start: startDateTime,
        end: endDateTime,
      });

      let message = `✅ Evento creato su Google Calendar!`;
      if (result.meet_link) message += `\n🔗 Link Google Meet: ${result.meet_link}`;
      alert(message);
    } catch (error) {
      console.error("Errore durante la creazione evento:", error);
      alert("❌ Errore nella creazione evento su Google Calendar");
    }
  };

  const filteredData = Array.isArray(leads)
    ? leads.filter((lead) => {
        const matchesSearch =
          lead.nome.toLowerCase().includes(searchText.toLowerCase()) ||
          lead.email.toLowerCase().includes(searchText.toLowerCase()) ||
          lead.telefono.toLowerCase().includes(searchText.toLowerCase());

        const matchesStatus = statusFilter === "Tutti" || lead.stato === statusFilter;
        return matchesSearch && matchesStatus;
      })
    : [];

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const getLeadPriority = (lead) => {
    const statiFinali = ["Registrati", "Scartati"];
    if (!lead.data_scadenza || statiFinali.includes(lead.stato)) return "normal";

    const today = new Date();
    const scadenza = new Date(lead.data_scadenza);
    const diffDays = Math.ceil((scadenza - today) / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return "expired";
    if (diffDays <= prioritySettings.urgentDays) return "urgent";
    return "normal";
  };

  useEffect(() => {
    const savedSearch = localStorage.getItem("leadTableSearch") || "";
    const savedFilter = localStorage.getItem("leadTableStatus") || "Tutti";
    setSearchText(savedSearch);
    setStatusFilter(savedFilter);
  }, []);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchText(value);
    localStorage.setItem("leadTableSearch", value);
  };

  const handleFilterChange = (e) => {
    const value = e.target.value;
    setStatusFilter(value);
    localStorage.setItem("leadTableStatus", value);
  };

  const handleDeleteLead = async (id) => {
    if (window.confirm("Sei sicuro di voler eliminare questo lead?")) {
      try {
        await deleteLead(id);
        setLeads((prev) => prev.filter((lead) => lead.id !== id));
        alert("✅ Lead eliminato con successo!");
      } catch (error) {
        console.error("Errore durante l'eliminazione del lead:", error);
        alert("❌ Errore durante l'eliminazione del lead.");
      }
    }
  };

  const columns = [
    { accessorKey: "nome", header: "Nome", cell: ({ row }) => <div>{row.original.nome}</div> },
    {
      accessorKey: "telefono",
      header: "Telefono",
      cell: ({ row }) =>
        editingCell.id === row.original.id && editingCell.field === "telefono" ? (
          <input
            className="p-1 border rounded w-full"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={stopEditing}
            onKeyDown={handleKeyDown}
            autoFocus
          />
        ) : (
          <div onClick={() => startEditing(row.original.id, "telefono", row.original.telefono)}>{row.original.telefono}</div>
        ),
    },
    {
      accessorKey: "email",
      header: "Email",
      cell: ({ row }) =>
        editingCell.id === row.original.id && editingCell.field === "email" ? (
          <input
            className="p-1 border rounded w-full"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={stopEditing}
            onKeyDown={handleKeyDown}
            autoFocus
          />
        ) : (
          <div onClick={() => startEditing(row.original.id, "email", row.original.email)}>{row.original.email}</div>
        ),
    },
    { accessorKey: "stato", header: "Stato", cell: ({ row }) => <div>{row.original.stato}</div> },
    {
      accessorKey: "note",
      header: "Note",
      cell: ({ row }) =>
        editingCell.id === row.original.id && editingCell.field === "note" ? (
          <textarea
            className="p-1 border rounded w-full"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={stopEditing}
            onKeyDown={handleKeyDown}
            autoFocus
          />
        ) : (
          <div onClick={() => startEditing(row.original.id, "note", row.original.note)}>{row.original.note}</div>
        ),
    },
    {
      accessorKey: "data_scadenza",
      header: "Scadenza",
      cell: ({ row }) =>
        editingCell.id === row.original.id && editingCell.field === "data_scadenza" ? (
          <input
            type="date"
            className="p-1 border rounded w-full"
            value={editValue ? editValue.split("T")[0] : ""}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={stopEditing}
            onKeyDown={handleKeyDown}
            autoFocus
          />
        ) : (
          <div onClick={() => startEditing(row.original.id, "data_scadenza", row.original.data_scadenza)}>
            {row.original.data_scadenza ? formatDate(row.original.data_scadenza) : "—"}
          </div>
        ),
    },
    {
      id: "azioni",
      header: "Azioni",
      cell: ({ row }) => (
        <div className="flex flex-wrap gap-2">
          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
              <button className="p-2 rounded hover:bg-gray-200">⋯</button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Content className="bg-white rounded shadow p-2">
              {statuses.map((status) => (
                <DropdownMenu.Item
                  key={status.id ?? status.name}
                  onSelect={() => handleStatusChange(row.original, status.name)}
                  className="p-2 hover:bg-gray-100 cursor-pointer"
                >
                  Sposta in {status.name}
                </DropdownMenu.Item>
              ))}
            </DropdownMenu.Content>
          </DropdownMenu.Root>

          <button
            onClick={() => handleAddToCalendar(row.original)}
            className="bg-green-500 text-white px-2 py-1 rounded hover:bg-green-600 text-xs"
          >
            📅
          </button>

          <a
            href={`https://calendly.com/ciao-labelfy/30min?name=${encodeURIComponent(row.original.nome)}&email=${encodeURIComponent(
              row.original.email
            )}&a1=${encodeURIComponent(row.original.telefono)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-indigo-500 text-white px-2 py-1 rounded hover:bg-indigo-600 text-xs"
          >
            Calendly
          </a>

          <button onClick={() => navigate(`/lead/${row.original.id}`)} className="bg-indigo-500 text-white px-2 py-1 rounded">
            👁️ Visualizza
          </button>

          <button
            onClick={() => handleDeleteLead(row.original.id)}
            className="bg-red-500 text-white px-2 py-1 rounded hover:bg-red-600 text-xs"
          >
            🗑️
          </button>
        </div>
      ),
    },
  ];

  const table = useReactTable({
    data: filteredData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualPagination: true,
  });

  return (
    <div className="overflow-x-auto max-w-full">
      <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-4">
        <input
          type="text"
          placeholder="Cerca lead..."
          className="p-2 border rounded w-full sm:w-1/2"
          value={searchText}
          onChange={handleSearchChange}
        />

        <select className="p-2 border rounded w-full sm:w-auto" value={statusFilter} onChange={handleFilterChange}>
          <option value="Tutti">Tutti</option>
          {statuses.map((status) => (
            <option key={status.id ?? status.name} value={status.name}>
              {status.name}
            </option>
          ))}
        </select>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full bg-white rounded-xl shadow text-sm">
          <thead className="bg-gray-100">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th key={header.id} className="text-left p-4">
                    {header.isPlaceholder ? null : (
                      <div onClick={header.column.getToggleSortingHandler()} className="cursor-pointer select-none">
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {header.column.getIsSorted() === "asc" ? " 🔼" : header.column.getIsSorted() === "desc" ? " 🔽" : ""}
                      </div>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr
                key={row.id}
                id={`lead-${row.original.id}`}
                className={`
                  ${getLeadPriority(row.original) === "expired" ? "border-l-4 border-red-500" : ""}
                  ${getLeadPriority(row.original) === "urgent" ? "border-l-4 border-yellow-400" : ""}
                  ${highlightedLeadId === row.original.id ? "bg-yellow-100 animate-pulse" : ""}
                `}
              >
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className="p-4 align-top">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default LeadTable;
